import { useEffect } from 'react';
import { Appointment } from '@medplum/fhirtypes';
import { AppointmentDetailData, AppointmentEvent } from '../types';

interface UseAlwaysLatestSelectedAppointmentParams {
  appointmentEvents: AppointmentEvent[];
  selectedAppointment: AppointmentDetailData | null;
  setSelectedAppointment: (value: (prev: AppointmentDetailData | null) => AppointmentDetailData | null) => void;
  getActivityDefinitionForAppointmentType: (code: string | undefined) => any;
  transformAppointmentToEvent: (
    appointment: Appointment,
    getActivityDefinitionForAppointmentType: any
  ) => AppointmentEvent;
}

/**
 * Hook to ensure the selected appointment is always up to date with the latest subscription event
 */
export function useAlwaysLatestSelectedAppointment({
  appointmentEvents,
  selectedAppointment,
  setSelectedAppointment,
  getActivityDefinitionForAppointmentType,
  transformAppointmentToEvent,
}: UseAlwaysLatestSelectedAppointmentParams): void {
  useEffect(() => {
    if (!selectedAppointment?.appointmentResource.id) {
      return;
    }

    const latestEvent = appointmentEvents.find(
      (event): event is AppointmentEvent =>
        'appointmentResource' in event && event.appointmentResource.id === selectedAppointment.appointmentResource.id
    );

    if (!latestEvent) {
      return;
    }

    setSelectedAppointment((prev) => {
      if (!prev || prev.appointmentResource.id !== latestEvent.appointmentResource.id) {
        return prev;
      }

      const transformed = transformAppointmentToEvent(
        latestEvent.appointmentResource,
        getActivityDefinitionForAppointmentType
      );

      return {
        ...prev,
        ...transformed,
        title: typeof latestEvent.title === 'string' ? String(latestEvent.title ?? '') : undefined,
        appointmentResource: latestEvent.appointmentResource,
      };
    });
  }, [
    appointmentEvents,
    getActivityDefinitionForAppointmentType,
    selectedAppointment?.appointmentResource.id,
    setSelectedAppointment,
    transformAppointmentToEvent,
  ]);
}
